/**
 * Search or jump (admin-design-r1, B's palette as a second way in): every section, plus whatever commands the page
 * adds. ⌘K or Ctrl K opens it (the AppShell listens), arrows move, Enter runs, Esc closes.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useId, useMemo, useState, type KeyboardEvent } from 'react'
import { cx } from '../cx'
import { Kbd } from '../primitives/Chip'
import { Icon, type IconName } from '../primitives/Icon'
import { usePortal } from '../portal'
import s from './CommandPalette.module.css'

export interface Command {
  id: string
  label: string
  group: string
  hint?: string
  icon?: IconName
  /** Extra words that should find it. */
  keywords?: string
  run: () => void
}

/** Commands whose label, group or keywords contain every word typed, in their given order. */
export function matchCommands(commands: Command[], query: string): Command[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return commands
  return commands.filter((c) => {
    const hay = `${c.label} ${c.group} ${c.keywords ?? ''}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}

export function CommandPalette({ open, onOpenChange, commands, placeholder = 'Search or jump to…' }: { open: boolean; onOpenChange: (open: boolean) => void; commands: Command[]; placeholder?: string }) {
  const portal = usePortal()
  const [query, setQuery] = useState('')
  const [at, setAt] = useState(0)
  const listId = useId()
  const found = useMemo(() => matchCommands(commands, query), [commands, query])
  const active = found[Math.min(at, found.length - 1)]

  const close = (o: boolean) => {
    if (!o) setQuery(''), setAt(0)
    onOpenChange(o)
  }
  const runIt = (c: Command | undefined) => {
    if (!c) return
    close(false)
    c.run()
  }
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') e.preventDefault(), setAt((i) => Math.min(i + 1, found.length - 1))
    else if (e.key === 'ArrowUp') e.preventDefault(), setAt((i) => Math.max(i - 1, 0))
    else if (e.key === 'Enter') e.preventDefault(), runIt(active)
  }

  let lastGroup = ''
  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal container={portal ?? undefined}>
        <Dialog.Overlay className={s.scrim} />
        <Dialog.Content className={s.pal} aria-describedby={undefined}>
          <Dialog.Title className={s.sr}>Search or jump</Dialog.Title>
          <div className={s.in}>
            <Icon name="search" />
            <input
              className={s.q}
              autoFocus
              value={query}
              placeholder={placeholder}
              onChange={(e) => (setQuery(e.target.value), setAt(0))}
              onKeyDown={keys}
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={active ? `${listId}-${active.id}` : undefined}
              aria-label="Search or jump"
            />
            <span className={s.esc}>
              <Kbd>Esc</Kbd>
            </span>
          </div>
          <div className={s.list} role="listbox" id={listId} aria-label="Results">
            {found.length === 0 && <p className={s.none}>Nothing matches “{query}”.</p>}
            {found.map((c) => {
              const head = c.group !== lastGroup ? ((lastGroup = c.group), <div key={`g-${c.group}`} className={s.group} role="presentation">{c.group}</div>) : null
              return [
                head,
                <div
                  key={c.id}
                  id={`${listId}-${c.id}`}
                  role="option"
                  aria-selected={c === active}
                  className={cx(s.item, c === active && s.on)}
                  onMouseEnter={() => setAt(found.indexOf(c))}
                  onClick={() => runIt(c)}
                >
                  {c.icon && <Icon name={c.icon} size="sm" />}
                  <span>{c.label}</span>
                  {c.hint && <span className={s.hint}>{c.hint}</span>}
                </div>,
              ]
            })}
          </div>
          <div className={s.foot}>
            <span><Kbd>↑</Kbd> <Kbd>↓</Kbd> move</span>
            <span><Kbd>Enter</Kbd> open</span>
            <span><Kbd>Esc</Kbd> close</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
